export type CmtSide = 'MH' | 'KD'

export type CmtItem = { t: string; c: string; u?: string }

export type ChatItem = CmtItem & { side: CmtSide }

export function parseCmt(raw: string | null | undefined): CmtItem[] {
  if (!raw || !String(raw).trim()) return []
  try {
    const parsed = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed
      .filter((x) => x && (x.c != null || x.t != null))
      .map((x) => ({
        t: String(x.t || ''),
        c: String(x.c || ''),
        u: x.u != null ? String(x.u) : undefined,
      }))
  } catch {
    return []
  }
}

/** Merge MH + KD theo thời gian (kiểu Zalo) */
export function buildChat(
  cmtMuaHang: string | null | undefined,
  cmtKinhDoanh: string | null | undefined,
): ChatItem[] {
  const mh = parseCmt(cmtMuaHang).map((x) => ({ ...x, side: 'MH' as const }))
  const kd = parseCmt(cmtKinhDoanh).map((x) => ({ ...x, side: 'KD' as const }))
  return [...mh, ...kd].sort((a, b) => String(a.t).localeCompare(String(b.t)))
}

/** Text xem cột list: từng dòng "HH:mm DD/MM · user: nội dung" */
export function formatCmtPreviewLines(
  raw: string | null | undefined,
  formatTime: (t: string) => string,
): string[] {
  return parseCmt(raw).map((item) => {
    const time = formatTime(item.t)
    const who = item.u ? `${item.u}: ` : ''
    const body = String(item.c || '').trim()
    return [time, who + body].filter(Boolean).join(' · ')
  })
}

/** Preview đoạn chat mix MH+KD theo thời gian */
export function formatMixedChatPreviewLines(
  cmtMuaHang: string | null | undefined,
  cmtKinhDoanh: string | null | undefined,
  formatTime: (t: string) => string,
): string[] {
  return buildChat(cmtMuaHang, cmtKinhDoanh).map((item) => {
    const time = formatTime(item.t)
    const side = item.side === 'MH' ? 'MH' : 'KD'
    const who = item.u ? `${item.u}` : side
    const body = String(item.c || '').trim()
    return `${time} · [${side}] ${who}: ${body}`
  })
}

/** Ghép text export Excel (mỗi tin 1 dòng) */
export function cmtSideTextForExport(
  raw: string | null | undefined,
  formatTime: (t: string) => string,
): string {
  return formatCmtPreviewLines(raw, formatTime).join('\n')
}

export function doanChatTextForExport(
  cmtMuaHang: string | null | undefined,
  cmtKinhDoanh: string | null | undefined,
  formatTime: (t: string) => string,
): string {
  return formatMixedChatPreviewLines(cmtMuaHang, cmtKinhDoanh, formatTime).join('\n')
}

/** Lấy nội dung comment từ HTML thông báo CMTYEUCAUMUA */
export function extractCmtFromNotificationContent(html: string | null | undefined): string {
  if (!html) return ''
  const text = String(html)
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .trim()
  // "Có comment mới trên yêu cầu mua MA: nội dung"
  const idx = text.lastIndexOf(':')
  if (idx < 0) return ''
  return text.slice(idx + 1).trim()
}

/** Tìm index tin trong đoạn chat khớp thông báo vừa click */
export function findHighlightChatIndex(
  chat: ChatItem[],
  highlightCmt: string | null | undefined,
  highlightAt?: string | null,
): number {
  const needle = String(highlightCmt || '').trim()
  if (!needle || !chat.length) return -1

  const exact = chat
    .map((item, idx) => ({ item, idx }))
    .filter(({ item }) => String(item.c || '').trim() === needle)

  const pool =
    exact.length > 0
      ? exact
      : chat
          .map((item, idx) => ({ item, idx }))
          .filter(({ item }) => {
            const body = String(item.c || '').trim()
            return body.includes(needle) || needle.includes(body)
          })

  if (pool.length === 0) return -1
  if (pool.length === 1) return pool[0].idx

  if (highlightAt) {
    const t0 = Date.parse(String(highlightAt))
    if (!Number.isNaN(t0)) {
      let best = pool[0]
      let bestDiff = Infinity
      for (const row of pool) {
        const t1 = Date.parse(String(row.item.t || ''))
        if (Number.isNaN(t1)) continue
        const diff = Math.abs(t1 - t0)
        if (diff < bestDiff) {
          bestDiff = diff
          best = row
        }
      }
      return best.idx
    }
  }

  return pool[pool.length - 1].idx
}
